import { TriggerKey } from "@redotech/redo-flows-common/triggers";
import { ClassifierAction } from "@redotech/redo-marketing-templates-common/recover-config";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export const AgeGateStatus = {
  NotVerified: "not_verified",
  VerifiedTrue: "verified_true",
  VerifiedFalse: "verified_false",
} as const;
export type AgeGateStatus = (typeof AgeGateStatus)[keyof typeof AgeGateStatus];

const marketingConversationTrackingDataSchema_ = z.object({
  /** The recover config associated with this conversation used for generating message content */
  recoverConfigId: zExt.objectId().optional(),
  /** The classifier action that produced this message */
  classifierAction: createEnum(ClassifierAction).optional(),
  // Automation context for tracking
  automationId: zExt.objectId().optional(),
  automationStepId: z.string().optional(),
  automationExecutionId: z.string().optional(),
  /** Account-owned strategy resolved for this invocation. */
  strategyId: z.string().optional(),
  /** Customer event that triggered the automation, when available.*/
  customerEventId: z.string().optional(),
  /** Additional trigger keys that were buffered with the primary trigger. */
  additionalTriggerKeys: z
    .array(z.string().transform((s) => s as TriggerKey))
    .optional(),
  /** Additional customer events that were buffered with the primary trigger. */
  additionalCustomerEventIds: z.array(z.string()).optional(),
  /** Sentinel decision that deferred this outreach; absent for immediate sends. */
  deferredFromDecisionId: z.string().optional(),
  /** Timestamp of when the automation associated with this conversation was triggered */
  automationTriggeredAt: z.coerce.date().optional(),
  /** Trigger key for quiet hours and smart sending decisions */
  triggerKey: z
    .string()
    .transform((s) => s as TriggerKey)
    .optional(),
  /** Snapshot of the content upon creation of a draft message, used to show contractor the original message if message was edited and flagged */
  originalContent: z.string().optional(),
  /**
   * A/B test treatment ID - set via $setOnInsert to preserve first assignment
   * Only set AFTER skip checks pass in the workflow, so only "reachable" customers have this
   */
  treatmentId: zExt.objectId().optional(),
  /**
   * Whether this treatment is the control group (no message sent)
   * Passed through the signal so the workflow can check without DB lookup
   */
  isControl: z.boolean().optional(),
  /** Timestamp of when customer most recently entered the recover automation (updated on each entry) */
  enteredAutomationAt: z.coerce.date().optional(),
  /** Email of the contractor who last sent or edited the message */
  contractorEmail: z.string().optional(),
  /** Whether this conversation uses iMessage sending (via Mac Mini) */
  isIMessage: z.boolean().optional(),
  /** Whether this conversation was routed to RCS at draft time. */
  isRcs: z.boolean().optional(),
  /** the associated concierge support conversation id */
  supportConversationId: zExt.objectId().optional(),
  /** the associated concierge support message id */
  supportMessageId: zExt.objectId().optional(),
});
export const marketingConversationTrackingDataSchema: z.ZodType<MarketingConversationTrackingData> =
  marketingConversationTrackingDataSchema_;
export interface MarketingConversationTrackingData extends z.infer<
  typeof marketingConversationTrackingDataSchema_
> {}

const marketingConversationConfigSchema_ =
  marketingConversationTrackingDataSchema_.extend({
    _id: zExt.objectId(),
    teamId: zExt.objectId(),
    marketingConversationId: zExt.objectId(),
    ageGateStatus: createEnum(AgeGateStatus)
      .optional()
      .default(AgeGateStatus.NotVerified),
    createdAt: z.date(),
    updatedAt: z.date(),
  });

export const marketingConversationConfigSchema: z.ZodType<MarketingConversationConfig> =
  marketingConversationConfigSchema_;

export interface MarketingConversationConfig extends z.infer<
  typeof marketingConversationConfigSchema_
> {}
